import VuexPersistence from 'vuex-persist'
import dayjs from 'dayjs'
import { SESSION_TYPES } from '@/enums'

// * Component that retrieve data from localStorage for data persistance
export default new VuexPersistence({
  key: 'innso-chat-storage',
  storage: window.localStorage,
  modules: [
    'Widget',
    'Messages',
    'LiveChat',
    'PreQualification',
    'DateTime',
    'Bot',
    'Sidebar',
    'Queuing',
    'Dydu',
  ],
  // *State reducer. reduces state to only those values we want to save.
  reducer: (state) => {
    const { draggableReady, ...widgetStateReduced } = state.Widget

    return {
      labels: state.labels,
      fetchedLabelTypes: state.fetchedLabelTypes,
      version: state.version,
      Widget: widgetStateReduced,
      Messages: state.Messages,
      DateTime: state.DateTime,
      Sidebar: state.Sidebar,
      Queuing: state.Queuing,
      Editor: {
        messageToSend: state.Editor.messageToSend,
      },
      LiveChat: {
        userIdentity: state.LiveChat.userIdentity,
        oldUserIdentity: state.LiveChat.oldUserIdentity,
        channelSid: state.LiveChat.channelSid,
        sendByMail: state.LiveChat.sendByMail,
        welcomeMessageSent: state.LiveChat.welcomeMessageSent,
      },
      PreQualification: state.PreQualification,
      Bot: {
        botConversationId: state.Bot.botConversationId,
        botInProgress: state.Bot.botInProgress,
        proactiveRules: state.Bot.proactiveRules,
        botHistory: state.Bot.botHistory,
        botSessionsCount: state.Bot.botSessionsCount,
        botHandoff: state.Bot.botHandoff,
        botHandoffMessageSent: state.Bot.botHandoffMessageSent,
        botWelcomeMessageSent: state.Bot.botWelcomeMessageSent,
      },
      Dydu: {
        dyduAuth: state.Dydu.dyduAuth,
        samlEnable: state.Dydu.samlEnable,
      },
    }
  },
  // * Function handles retrieving state from storage
  restoreState: (key, storage) => {
    let innsoChatStorage = storage.getItem(key)

    if (typeof innsoChatStorage !== 'string') {
      return {}
    }

    innsoChatStorage = JSON.parse(innsoChatStorage || '{}')
    // Check if previous version of the chatbox is the same or flush persisted data
    // Or if previous client identity is over expirationTime
    if (
      innsoChatStorage?.version !== process.env.VUE_APP_VERSION ||
      clientIdentityHasExpired(innsoChatStorage)
    ) {
      const widget = {
        chatInApp: innsoChatStorage?.Widget?.chatInApp,
      }

      if (widget.chatInApp && innsoChatStorage?.Widget?.externalIdentity) {
        console.info('Chat in app: persist contact sid')
        widget.externalIdentity = innsoChatStorage.Widget.externalIdentity
      }

      return {
        Widget: widget,
      }
    }

    if (innsoChatStorage) {
      innsoChatStorage.Messages.messages = limitStoredMessages(
        innsoChatStorage?.Messages.messages,
      )
    }

    // Check if current session is still valid, if not preserve only few data
    if (!currentSessionIsValid(innsoChatStorage)) {
      console.info(
        `Restore state: Current ${innsoChatStorage.Widget.sessionType} session is not valid`,
      )

      return {
        Widget: innsoChatStorage.Widget,
        PreQualification: innsoChatStorage.PreQualification,
        LiveChat: {
          welcomeMessageSent: innsoChatStorage.LiveChat.welcomeMessageSent,
        },
        Bot: {
          botWelcomeMessageSent: innsoChatStorage.Bot.botWelcomeMessageSent,
        },
        Messages: {
          messages: innsoChatStorage.Messages.messages,
        },
        DateTime: innsoChatStorage.DateTime,
        Dydu: {
          dyduAuth: innsoChatStorage.Dydu.dyduAuth,
          samlEnable: innsoChatStorage.Dydu.samlEnable,
        },
      }
    }

    // Reuse full local storage
    return innsoChatStorage
  },
  // * Mutation filter: only those ones which you want a persistence write to be triggered for
  filter: (mutation) => {
    const mutationFilter = [
      'SET_LABELS',
      'UPDATE_SKILL',
      'RESET',
      // WIDGET
      'Widget/UPDATE_WIDGET_STATE',
      'Widget/UPDATE_SESSION_TYPE',
      'Widget/UPDATE_CSS',
      'Widget/UPDATE_FULLFRAME_OPTIONS',
      'Widget/UPDATE_TEASER_OPTIONS',
      'Widget/UPDATE_AUTOCLOSE_PARAMETERS',
      'Widget/UPDATE_RGPD',
      'Widget/UPDATE_SHIFT',
      'Widget/UPDATE_SHIFT_INITIATED',
      'Widget/SET_SHIFT_INITIAL',
      'Widget/UPDATE_EXTERNAL_IDENTITY',
      'Widget/UPDATE_LAST_VISIT_TIMESTAMP',
      'Widget/UPDATE_LAST_TARGET_RULE',
      'Widget/UPDATE_CLOSE_TIMESTAMP',
      'Widget/UPDATE_CLOSE_WIDGET',
      'Widget/UPDATE_WIDGET_WARNING_DATA',
      'Widget/UPDATE_WIDGET_WARNING_TIMESTAMP',
      'Widget/Reset',
      // EDITOR
      'Editor/SET_MESSAGE',
      'Editor/CLEAR_MESSAGE',
      'Editor/Reset',
      // LIVE CHAT
      'LiveChat/UPDATE_USER_IDENTITY',
      'LiveChat/SET_OLD_USER_IDENTITY',
      'LiveChat/RESET_OLD_USER_IDENTITY',
      'LiveChat/UPDATE_CHANNEL_SID',
      'LiveChat/UPDATE_SEND_BY_MAIL',
      'LiveChat/UPDATE_WELCOME_MESSAGE_SENT',
      'LiveChat/Reset',
      // MESSAGES
      'Messages/UPDATE_LAST_MESSAGE_TIMESTAMP',
      'Messages/UPDATE_MESSAGES_BOT',
      'Messages/UPDATE_MESSAGES_CHAT',
      'Messages/INCREASE_UNREAD_MESSAGES',
      'Messages/RESET_UNREAD_MESSAGES',
      'Messages/UPDATE_FEEDBACK_SID',
      'Messages/UPDATE_FEEDBACK_STEP',
      'Messages/UPDATE_MESSAGES_STEP_ACTIONS',
      'Messages/UPDATE_HOME_SID',
      'Messages/REMOVE_MESSAGE_BY_SID',
      'Messages/SET_MESSAGE_SATISFACTION_COMPLETED',
      'Messages/UPDATE_SCROLL_TO_LAST_SELECTOR',
      'Message/Reset',
      // PRE QUALIFICATION
      'PreQualification/SET_FORM_DATA',
      'PreQualification/SET_SKILL_ROUTING',
      'PreQualification/SET_COMPLETED',
      // DATE-TIME
      'DateTime/SET_WIDGET_HIDE_TIMESTAMP',
      'DateTime/RESET_WIDGET_HIDE_TIMESTAMP',
      'DateTime/TIMESTAMP_MESSAGE_SENT',
      // BOT
      'Bot/UPDATE_BOT_CONVERSATION_ID',
      'Bot/SET_BOT_IN_PROGRESS',
      'Bot/ADD_PROACTIVE_RULE',
      'Bot/SET_BOT_HISTORY',
      'Bot/INCREMENT_BOT_SESSIONS_COUNT',
      'Bot/SET_BOT_HANDOFF',
      'Bot/RESET_BOT_HANDOFF',
      'Bot/UPDATE_BOT_HANDOFF_MESSAGE_SENT',
      'Bot/UPDATE_BOT_WELCOME_MESSAGE_SENT',
      // DYDU
      'Dydu/SET_DYDU_AUTH',
      'Dydu/RESET_DYDU_AUTH',
      'Dydu/RESET_DYDU_AUTH',
      'Dydu/DISABLE_SAML',
      // SIDEBAR
      'Sidebar/UPDATE_SIDEBAR_VISIBLILITY',
      'Sidebar/UPDATE_SIDEBAR_ID',
      'Sidebar/UPDATE_RESIZED_SIZE',
      'Sidebar/RESET_RESIZED_SIZE',
      'Sidebar/RESET',
      // QUEUING
      'Queuing/UPDATE_MESSAGE',
      'Queuing/SAVE_FIRST_MESSAGE_SID',
      'Queuing/CLEAR_MESSAGE',
      'Queuing/SET_QUEUE_IS_CONNECTED',
    ]

    return mutationFilter.includes(mutation.type)
  },
})

/**
 * It's considered that client identity has expired if we're after persistencyContactSid delay based on :
 * - lastVisitTimeStamp (id creation)
 * - or lastMessageTimestamp if no valid lastVisitTimeStamp defined
 *
 * @param {*} { Messages, Widget }
 * @returns
 */
function clientIdentityHasExpired({ Messages, Widget }) {
  const now = dayjs()

  const lastVisitExpirationTime = dayjs(Widget.lastVisitTimeStamp).add(
    Widget.fullframeOptions.persistencyContactSid,
    'day',
  )

  const lastMessageExpirationTime = dayjs(Messages.lastMessageTimestamp).add(
    Widget.fullframeOptions.persistencyContactSid,
    'day',
  )

  const hasExpired =
    // no lastVisitTimeStamp
    !lastVisitExpirationTime.isValid() ||
    // OR last message if exist is out of time
    (lastMessageExpirationTime.isValid() &&
      now.isAfter(lastMessageExpirationTime)) ||
    // OR last lastVisitTimeStamp is out of time if last message does not exist
    (!lastMessageExpirationTime.isValid() &&
      now.isAfter(lastVisitExpirationTime))

  console.info(
    `Restore state: Client identity has ${hasExpired ? '' : 'not'} expired`,
  )

  return hasExpired
}
/**
 * Check if current session still have require data to go on
 *
 * @param {*} {
 *   Widget: { sessionType },
 *   LiveChat: { userIdentity, channelSid },
 *   Bot: { botConversationId },
 * }
 * @returns
 */
function currentSessionIsValid({
  Widget: { closeWidget, sessionType },
  LiveChat: { userIdentity, channelSid },
  Bot: { botConversationId },
}) {
  if (closeWidget.process) {
    return true
  }

  switch (sessionType) {
    case SESSION_TYPES.CHAT:
      return userIdentity !== '' && channelSid

    case SESSION_TYPES.BOT:
      return !!botConversationId

    default:
      return true
  }
}
function limitStoredMessages(messages) {
  if (messages.length > 100) {
    messages.splice(0, messages.length - 100)
  }
  return messages
}
